import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["actualDate", "children", "dayRef", "onClick", "customStyles"],
  _excluded2 = ["hovered"];
import useHovered from 'UIComponents/hooks/useHovered';
import { View } from 'foundations-components/components/View';
import { HoverMarker } from '../HoverStyles';
import { DropTarget } from '../../dragDrop/internal/DropTarget';
import { CompactDay, CompactDayText, CompactDayWrapper } from './MonthCalendarStyles';
import { useCalendarProps } from '../../internal/CalendarPropsProvider';
import { styled, useTheme } from '../../lib/styles';
import { DateMarker } from '../../components/DateMarker';
import { AddIcon } from 'foundations-assets/icons/components/AddIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DayText = ({
  isCurrentDay,
  isOutsideMonth = false,
  children
}) => {
  const {
    uiAddonCalendars: {
      dateNumberHeight
    }
  } = useTheme();
  return /*#__PURE__*/_jsx(DateMarker, {
    isCurrentDay: isCurrentDay,
    size: dateNumberHeight,
    position: "absolute",
    insetInlineStart: "200",
    insetBlockStart: "200",
    textProps: {
      color: isOutsideMonth ? 'text-primary-disabled' : undefined,
      typography: 'detail-200'
    },
    children: children
  });
};
const CreateButtonElement = styled.button.withConfig({
  displayName: "MonthDay__CreateButtonElement",
  componentId: "sc-1elr2zv-0"
})(({
  theme: {
    uiAddonCalendars,
    color,
    borderRadius
  }
}) => `
  inline-size: ${uiAddonCalendars.dateNumberHeight};
  block-size: ${uiAddonCalendars.dateNumberHeight};
  background-color: ${color['fill-accent-neutral-default']};
  border-radius: ${borderRadius['100']};
  padding: 0;
  border: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &&:hover {
    background-color: ${color['fill-accent-neutral-hover']};
  }
  &&:active {
    background-color: ${color['fill-accent-neutral-pressed']};
  }
`);
const CreateButton = ({
  droppedMoment,
  thisMoment
}) => {
  const {
    onClickToCreate
  } = useCalendarProps();
  return /*#__PURE__*/_jsx(View, {
    position: "absolute",
    insetInlineEnd: "200",
    insetBlockStart: "200",
    children: /*#__PURE__*/_jsx(CreateButtonElement, {
      "data-test-id": `click-to-create-button-MONTH-day-${droppedMoment.format('D-M-YYYY')}`,
      onClick: () => onClickToCreate(thisMoment),
      children: /*#__PURE__*/_jsx(AddIcon, {
        color: "text-core-on-fill-default",
        size: "100"
      })
    })
  });
};
const DayView = _ref => {
  let {
      actualDate,
      children,
      dayRef,
      onClick,
      customStyles
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  return /*#__PURE__*/_jsx(View, Object.assign({
    position: "relative",
    borderInlineEndWidth: "100",
    borderInlineEndStyle: "solid",
    borderInlineEndColor: "border-core-subtle",
    blockSize: theme.uiAddonCalendars.monthView.dayHeight,
    minBlockSize: "137px",
    flex: "1 1 auto"
  }, customStyles, theme.uiAddonCalendars.util.getTemporalModifiers({
    forMoment: actualDate,
    magnitude: 'day'
  }), {
    children: /*#__PURE__*/_jsx("div", Object.assign({
      ref: dayRef,
      onClick: onClick
    }, rest, {
      style: {
        height: '100%'
      },
      children: children
    }))
  }));
};
export default function MonthDay({
  actualDate,
  children,
  compact,
  droppedMoment,
  isCurrentDay,
  thisMoment,
  isOutsideMonth,
  isToday
}) {
  const _useHovered = useHovered({}),
    {
      hovered
    } = _useHovered,
    hoveredProps = _objectWithoutPropertiesLoose(_useHovered, _excluded2);
  const {
    onClickToCreateAvailable,
    onClickTime
  } = useCalendarProps();
  const commonProps = {
    'data-test-id': `MONTH-day-${droppedMoment.format('D-M-YYYY')}`,
    'data-test-meta-day': droppedMoment.format('dddd')
  };
  if (compact) {
    return /*#__PURE__*/_jsx(CompactDayWrapper, {
      children: /*#__PURE__*/_jsxs(CompactDay, Object.assign({}, commonProps, {
        isCurrentDay: isCurrentDay,
        isOutsideMonth: isOutsideMonth,
        isToday: isToday,
        onClick: e => {
          onClickTime(thisMoment, e);
        },
        children: [/*#__PURE__*/_jsx(HoverMarker, {
          canDrop: false,
          isOver: false,
          isSameTime: false
        }), /*#__PURE__*/_jsx(CompactDayText, {
          children: actualDate.date()
        }), children]
      }))
    });
  }
  return /*#__PURE__*/_jsx(DropTarget, {
    droppedMoment: droppedMoment,
    viewType: "MONTH",
    render: ({
      isDragging,
      isSameTime,
      isOver,
      canDropResult: canDrop,
      containerRef,
      dropTarget
    }) => /*#__PURE__*/_jsxs(DayView, Object.assign({}, commonProps, hoveredProps, {
      dayRef: containerRef,
      actualDate: actualDate,
      onClick: e => {
        onClickTime(thisMoment, e);
      },
      children: [/*#__PURE__*/_jsx(HoverMarker, {
        canDrop: canDrop,
        isOver: isOver,
        isSameTime: isSameTime
      }), /*#__PURE__*/_jsx(DayText, {
        isCurrentDay: isCurrentDay,
        isOutsideMonth: isOutsideMonth,
        children: actualDate.date()
      }), onClickToCreateAvailable && hovered && !isDragging && /*#__PURE__*/_jsx(CreateButton, {
        droppedMoment: droppedMoment,
        thisMoment: thisMoment
      }), children, dropTarget]
    }))
  });
}